import { useMemo } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { ArrowLeft, ClipboardList, Pencil, Plus, SlidersHorizontal } from "lucide-react";
import { StatusBadge, type StatusTone } from "@fawkes/ui";

import { speciesIconSrc } from "../components/AnimalOverview";

/**
 * De instellingen van het afstandsportaal: welke vragen de eigenaar te zien krijgt, per diersoort.
 *
 * <p><b>Dezelfde opzet als nazorg.</b> Een afstandsvragenlijst is dezelfde soort vragenlijst als een
 * nazorglijst — zelfde bouwer, zelfde stappen en velden, zelfde concept- en publiceerstatus — alleen met
 * "afstand" als onderwerp en een diersoort erbij (Maarten, 2026-10-23: "die moet je een beetje zien als
 * nazorg en wizards"). Dit scherm maakt de vragen dus niet zelf; het laat zien wat er staat en stuurt je
 * naar de bouwer.
 *
 * <p><b>Waarom per diersoort.</b> Wat je wilt weten over een hond die weg moet — hoe is hij met kinderen,
 * kan hij alleen thuis zijn — slaat nergens op bij een cavia. Een diersoort zonder eigen lijst valt terug
 * op de algemene, zodat een asiel met één lijst kan beginnen.
 *
 * <p><b>Een concept telt niet mee.</b> Het portaal toont alleen gepubliceerde lijsten; daarom staat de
 * status erbij, en daarom is een diersoort met alleen een concept hier zichtbaar als "nog niet live".
 */

interface Vragenlijst {
  id: string;
  naam: string;
  diersoort: string;
  status: string;
  actief: boolean;
}

interface Diersoort {
  id: number;
  label: string;
}

const STATUS_TOON: Record<string, StatusTone> = {
  GEPUBLICEERD: "success",
  CONCEPT: "warning",
  GEARCHIVEERD: "neutral",
};

export function WachtlijstInstellingen({ onBack, onOpenBouwer }: {
  onBack: () => void;
  /** Opent de vragenlijstbouwer met deze definitie open. */
  onOpenBouwer: (definitieId: string) => void;
}) {
  const t = useTranslate();
  const { data, isLoading, isError, refetch } = useCustom<Vragenlijst[]>({
    url: "wachtlijst/vragenlijsten",
    method: "get",
  });
  // Dezelfde bron als de inboekwizard gebruikt; een eigen diersoortenfeed bestaat niet.
  const { data: soortenData } = useCustom<Diersoort[]>({
    url: "intake/reference/diersoorten",
    method: "get",
    queryOptions: { retry: false },
  });
  const { mutate, isLoading: bezig } = useCustomMutation();

  const lijsten = useMemo(() => data?.data ?? [], [data]);
  const soorten = useMemo(() => soortenData?.data ?? [], [soortenData]);

  /**
   * Eén regel per diersoort die het asiel kent, plus de algemene lijst. Ook de diersoorten zónder lijst
   * staan erin: dat is juist wat je wilt zien — waar nog niets staat, krijgt de eigenaar de algemene
   * vragen, en dat is zelden wat je bedoelde.
   */
  const regels = useMemo(() => {
    const perSoort = new Map<string, Vragenlijst>();
    for (const l of lijsten) {
      // Een lijst zonder diersoort hoort hier niet meer te bestaan; zie de uitleg hierboven. Een
      // oude die er nog ligt tonen we niet, want hij wordt ook niet meer gebruikt.
      if (l.diersoort !== "Alle") perSoort.set(l.diersoort.toLowerCase(), l);
    }
    const uit: Array<{ diersoort: string; lijst?: Vragenlijst }> = [];
    const gebruikt = new Set<string>();
    for (const s of soorten) {
      const sleutel = s.label.toLowerCase();
      gebruikt.add(sleutel);
      uit.push({ diersoort: s.label, lijst: perSoort.get(sleutel) });
    }
    // Een lijst voor een diersoort die het asiel niet (meer) voert hoort toch zichtbaar te zijn:
    // anders is hij onvindbaar terwijl hij in het portaal wél gebruikt wordt.
    for (const [sleutel, lijst] of perSoort) {
      if (!gebruikt.has(sleutel)) {
        uit.push({ diersoort: lijst.diersoort, lijst });
      }
    }
    return uit;
  }, [lijsten, soorten, t]);

  const maak = (diersoort: string | null) => {
    mutate(
      {
        // Niet leeg: de lijst komt gevuld met de voorbeeldvragen, als concept. Een lege vragenlijst
        // is onbruikbaar, en de vragen waar het later op misgaat zijn juist de vragen die je zelf
        // niet bedenkt.
        url: `wachtlijst/vragenlijsten${diersoort ? `?diersoort=${encodeURIComponent(diersoort)}` : ""}`,
        method: "post",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          const id = (res?.data as { definitieId?: string } | undefined)?.definitieId;
          void refetch();
          // Meteen de bouwer in: je wilt de vragen nalopen voor je publiceert.
          if (id) onOpenBouwer(id);
        },
      },
    );
  };

  return (
    <div className="wl-inst">
      <header className="wl-inst__kop">
        <button type="button" className="wl-dossier__terug" onClick={onBack}>
          <ArrowLeft size={16} aria-hidden="true" /> {t("wachtlijstDetail.terug")}
        </button>
        <div className="wl-dossier__titel">
          <span className="wl-dossier__icoon" aria-hidden="true"><SlidersHorizontal size={24} /></span>
          <div>
            <h2>{t("wachtlijstInstellingen.titel")}</h2>
            <p>{t("wachtlijstInstellingen.onderschrift")}</p>
          </div>
        </div>
      </header>

      <section className="wl-dossier__blok">
        <h3><ClipboardList size={16} aria-hidden="true" /> {t("wachtlijstInstellingen.vragenlijsten")}</h3>
        <p className="fawkes-muted wl-dossier__uitleg">{t("wachtlijstInstellingen.uitleg")}</p>

        {isError && <p className="fawkes-error">{t("wachtlijstInstellingen.laadfout")}</p>}
        {isLoading ? (
          <p className="fawkes-muted">{t("common.loading")}</p>
        ) : (
          <ul className="wl-inst__lijst">
            {regels.map((r) => (
              <li key={r.diersoort} className="wl-inst__regel">
                <span className="wl-inst__soort">
                  <img className="wl-inst__icoon" src={speciesIconSrc(r.diersoort)} alt="" />
                  <span>
                    <strong>{r.diersoort}</strong>
                  </span>
                </span>

                {r.lijst ? (
                  <>
                    <span className="wl-inst__naam">{r.lijst.naam}</span>
                    <StatusBadge tone={STATUS_TOON[r.lijst.status] ?? "neutral"}>
                      {t(`wachtlijstInstellingen.status.${r.lijst.status}`)}
                    </StatusBadge>
                    <span className="wl-inst__knoppen">
                      <button type="button" className="kvo__instellingen"
                              onClick={() => onOpenBouwer(r.lijst!.id)}>
                        <Pencil size={14} aria-hidden="true" />
                        {t("wachtlijstInstellingen.aanpassen")}
                      </button>
                      {/* Ook met een bestaande lijst: opnieuw beginnen met de volledige voorbeeldvragen
                          is iets anders dan de huidige aanpassen, en allebei hoort te kunnen. */}
                      <button type="button" className="kvo__instellingen" disabled={bezig}
                              onClick={() => maak(r.diersoort)}>
                        <Plus size={14} aria-hidden="true" />
                        {t("wachtlijstInstellingen.nieuweLijst")}
                      </button>
                    </span>
                  </>
                ) : (
                  <>
                    {/* Geen lijst is geen fout, maar wel iets om te weten: voor deze diersoort krijgt
                        de eigenaar dan géén vragen. Er is geen algemene lijst om op terug te vallen. */}
                    <span className="wl-inst__naam fawkes-muted">
                      {t("wachtlijstInstellingen.geenVragen")}
                    </span>
                    <span />
                    <button type="button" className="kvo__instellingen" disabled={bezig}
                            onClick={() => maak(r.diersoort)}>
                      <Plus size={14} aria-hidden="true" />
                      {t("wachtlijstInstellingen.maken")}
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
